'use client';

import { useState, useActionState, startTransition, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useFormStatus } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { FileUploadField } from '../../components/ui/FileUploadField';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Terminal, CheckCircle, Users, Shield, Eye, EyeOff, User, Clock, AlertCircle, Loader2, LogIn } from 'lucide-react';
import { useAuth } from '../../hooks/use-auth';
import Link from 'next/link';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signOut,
  type User as FirebaseUser 
} from 'firebase/auth';
import { 
  doc, 
  setDoc,
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { auth, db, storage } from '../../lib/firebase';
import { COUNTRIES } from '../../lib/countries';
import { LANGUAGES } from '../../lib/languages';
import { INDUSTRIES } from '../../lib/industries';

const timezones = [
  "(GMT-12:00) International Date Line West",
  "(GMT-11:00) Coordinated Universal Time-11",
  "(GMT-10:00) Hawaii",
  "(GMT-09:00) Alaska",
  "(GMT-08:00) Pacific Time (US & Canada)",
  "(GMT-07:00) Mountain Time (US & Canada)",
  "(GMT-06:00) Central Time (US & Canada)",
  "(GMT-05:00) Eastern Time (US & Canada)",
  "(GMT-04:00) Atlantic Time (Canada)",
  "(GMT-03:00) Buenos Aires",
  "(GMT-02:00) Coordinated Universal Time-02",
  "(GMT-01:00) Azores",
  "(GMT+00:00) London, Dublin",
  "(GMT+01:00) Amsterdam, Berlin, Rome",
  "(GMT+02:00) Athens, Helsinki, Istanbul",
  "(GMT+03:00) Moscow, St. Petersburg",
  "(GMT+04:00) Dubai, Abu Dhabi",
  "(GMT+05:00) Islamabad, Karachi",
  "(GMT+06:00) Dhaka",
  "(GMT+07:00) Bangkok, Jakarta",
  "(GMT+08:00) Beijing, Hong Kong",
  "(GMT+09:00) Tokyo, Seoul",
  "(GMT+10:00) Sydney, Melbourne",
  "(GMT+11:00) Solomon Islands",
  "(GMT+12:00) Auckland, Wellington",
];

// ✅ Utility function to wait for profile documents to exist
async function waitForProfile(uid: string, maxAttempts: number = 10, delayMs: number = 500): Promise<boolean> {
  let attempts = 0;
  while (attempts < maxAttempts) {
    try {
      const [publicDocRef, privateDocRef] = [
        doc(db, 'interpreters', uid),
        doc(db, 'interpreters', uid, 'private', 'profile')
      ];
      
      const [publicSnap, privateSnap] = await Promise.all([
        getDoc(publicDocRef),
        getDoc(privateDocRef)
      ]);
      
      if (publicSnap.exists() && privateSnap.exists()) {
        console.log(`✅ Both profile documents confirmed for ${uid} after ${attempts + 1} attempts`);
        return true;
      }
      
      console.log(`⏳ Profile documents not ready yet, attempt ${attempts + 1}/${maxAttempts}`);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      attempts++;
    } catch (error) {
      console.warn(`⚠️ Error checking profile documents (attempt ${attempts + 1}):`, error);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      attempts++;
    }
  }
  
  console.warn(`⚠️ Profile documents not confirmed after ${maxAttempts} attempts`);
  return false;
}

// ✅ NEW: Profile picture upload function
async function uploadProfilePicture(file: File, userId: string): Promise<string> {
  try {
    console.log('🔄 Uploading profile picture...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      throw new Error('Please upload a valid image file.');
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      throw new Error('Image file size must be less than 5MB.');
    }
    
    // Create unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `profile_${timestamp}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `profile-pictures/${userId}/${fileName}`);
    
    // Upload file
    const uploadTask = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });
    
    console.log('✅ File uploaded successfully to:', uploadTask.ref.fullPath);
    
    // Get download URL
    const downloadURL = await getDownloadURL(uploadTask.ref);
    console.log('✅ Download URL obtained:', downloadURL);
    
    return downloadURL;
  } catch (error: any) {
    console.error('❌ Error uploading profile picture:', error);
    
    if (error.code === 'storage/unauthorized') {
      throw new Error('You do not have permission to upload files. Please try again.');
    } else if (error.code === 'storage/canceled') {
      throw new Error('File upload was canceled. Please try again.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('An unknown error occurred during upload. Please try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to upload profile picture. Please try again.');
    }
  }
}

function SubmitButton({ isRegistering }: { isRegistering: boolean }) {
  const { pending } = useFormStatus();
  const isDisabled = pending || isRegistering;
  
  return (
    <Button size="lg" className="w-full" type="submit" disabled={isDisabled}>
      {isDisabled ? (
        <>
          <Loader2 className="animate-spin h-4 w-4 mr-2" />
          {isRegistering ? 'Creating Account...' : 'Processing...'}
        </>
      ) : (
        <>
          <User className="mr-2 h-4 w-4" />
          Create Account
        </>
      )}
    </Button>
  );
}

function SignUpForm() {
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [registrationStep, setRegistrationStep] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  
  const router = useRouter();

  const methods = useForm({
    mode: 'onChange'
  });

  // ✅ Role-aware authenticated redirect: clients belong on /client-dashboard,
  // never /profile (that's interpreter-only and would bounce them right back).
  // Runs once per session, only once auth has settled to a real user, and
  // never signs anyone out or sends them to /login on a Firestore hiccup.
  const clientCheckedRef = useRef(false);
  const signupRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ✅ Post-registration redirect-to-login timer (set from handleRegistration,
  // outside a useEffect) — cleared on unmount in the effect below.
  const registrationRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (registrationRedirectTimerRef.current) {
        clearTimeout(registrationRedirectTimerRef.current);
        registrationRedirectTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user || isRegistering || registrationSuccess) {
      return undefined;
    }
    if (clientCheckedRef.current) {
      return undefined;
    }
    clientCheckedRef.current = true;

    let cancelled = false;

    (async () => {
      let destination = '/profile';
      try {
        const clientSnap = await getDoc(doc(db, 'clients', user.uid));
        if (clientSnap.exists()) {
          destination = '/client-dashboard';
        }
      } catch (error) {
        // Firestore read failed — don't guess wrong, just fall back to the
        // interpreter default rather than redirecting to /login.
        console.error('❌ Failed to determine account type, defaulting to /profile:', error);
      }

      if (cancelled) return;

      console.log(`👤 User already authenticated, redirecting to ${destination}...`);
      setIsRedirecting(true);

      // Add a small delay to prevent render conflicts
      signupRedirectTimerRef.current = setTimeout(() => {
        router.push(destination);
      }, 100);
    })();

    return () => {
      cancelled = true;
      if (signupRedirectTimerRef.current) {
        clearTimeout(signupRedirectTimerRef.current);
        signupRedirectTimerRef.current = null;
      }
    };
  }, [isAuthenticated, user, authLoading, isRegistering, registrationSuccess, router]);

  // ✅ ENHANCED: Registration function with file upload
  const handleRegistration = async (formData: FormData): Promise<void> => {
    setIsRegistering(true);
    setFormError(null);
    setRegistrationStep('Validating form data...');

    try {
      const firstName = formData.get('firstName') as string;
      const lastName = formData.get('lastName') as string;
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;
      const bio = formData.get('bio') as string;
      const city = (formData.get('city') as string)?.trim();
      const country = formData.get('country') as string;
      const location = city && country ? `${city}, ${country}` : '';
      const hourlyRate = formData.get('hourlyRate') as string;
      const halfDayRate = formData.get('halfDayRate') as string;
      const dailyRate = formData.get('dailyRate') as string;
      const ratePerWord = formData.get('ratePerWord') as string;
      const ratePerPage = formData.get('ratePerPage') as string;
      const translationHourlyRate = formData.get('translationHourlyRate') as string;
      const timeZone = formData.get('timeZone') as string;
      const whatsapp = formData.get('whatsapp') as string;
      const linkedin = formData.get('linkedin') as string;
      const keywords = formData.get('keywords') as string;
      const education = formData.get('education') as string;
      const culturalKnowledge = formData.get('culturalKnowledge') as string;

      // ✅ NEW: Get profile picture file
      const profilePictureFile = formData.get('profilePicture') as File;

      console.log('🚀 Starting Talksie interpreter registration (CLIENT-SIDE)...');
      console.log('Selected languages:', selectedLanguages);
      console.log('Selected industries:', selectedIndustries);
      console.log('Profile picture:', profilePictureFile ? `${profilePictureFile.name} (${profilePictureFile.size} bytes)` : 'No file');

      // Validate required data
      if (selectedLanguages.length === 0) {
        throw new Error('Please select at least one language.');
      }

      if (selectedIndustries.length === 0) {
        throw new Error('Please select at least one industry.');
      }

      // ✅ NEW: Validate profile picture
      if (!profilePictureFile || profilePictureFile.size === 0) {
        throw new Error('Profile picture is required.');
      }

      // Validate required fields
      if (!firstName || !lastName || !email || !password || !bio || !city || !country || !timeZone || !whatsapp || !linkedin || !keywords || !education || !culturalKnowledge) {
        throw new Error('Please fill in all required fields.');
      }

      if (bio.length < 50) {
        throw new Error('Bio must be at least 50 characters long.');
      }

      if (education.length < 10) {
        throw new Error('Education section must be at least 10 characters long.');
      }

      if (culturalKnowledge.length < 10) {
        throw new Error('Cultural knowledge section must be at least 10 characters long.');
      }

      if (keywords.length < 5) {
        throw new Error('Keywords must be at least 5 characters long.');
      }

      // Parse all rate fields
      const hourlyRateNum = hourlyRate ? parseFloat(hourlyRate) : undefined;
      const halfDayRateNum = halfDayRate ? parseFloat(halfDayRate) : undefined;
      const dailyRateNum = dailyRate ? parseFloat(dailyRate) : undefined;
      const ratePerWordNum = ratePerWord ? parseFloat(ratePerWord) : undefined;
      const ratePerPageNum = ratePerPage ? parseFloat(ratePerPage) : undefined;
      const translationHourlyRateNum = translationHourlyRate ? parseFloat(translationHourlyRate) : undefined;

      const hasInterpretingRate = (hourlyRateNum && hourlyRateNum > 0) || (halfDayRateNum && halfDayRateNum > 0) || (dailyRateNum && dailyRateNum > 0);
      const hasTranslationRate = (ratePerWordNum && ratePerWordNum > 0) || (ratePerPageNum && ratePerPageNum > 0) || (translationHourlyRateNum && translationHourlyRateNum > 0);
      if (!hasInterpretingRate && !hasTranslationRate) {
        throw new Error('Please fill in at least one rate (interpreting or translation).');
      }

      const rateFields = [hourlyRateNum, halfDayRateNum, dailyRateNum, ratePerWordNum, ratePerPageNum, translationHourlyRateNum].filter(r => r !== undefined);
      if (rateFields.some(r => r! <= 0)) {
        throw new Error('All rates must be positive numbers.');
      }

      setRegistrationStep('Creating user account...');
      console.log('🔄 Creating Firebase user account...');
      
      // ✅ CLIENT-SIDE: Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase user created:', firebaseUser.uid);

      // ✅ NEW: Upload profile picture BEFORE creating profile documents
      setRegistrationStep('Uploading profile picture...');
      const profilePictureURL = await uploadProfilePicture(profilePictureFile, firebaseUser.uid);
      console.log('✅ Profile picture uploaded:', profilePictureURL);

      setRegistrationStep('Setting up profile...');
      
      // ✅ ENHANCED: Update user profile with photo URL
      await updateProfile(firebaseUser, {
        displayName: `${firstName} ${lastName}`,
        photoURL: profilePictureURL
      });
      console.log('✅ User profile updated with photo URL');

      setRegistrationStep('Creating interpreter profile...');
      console.log('🔄 Creating Firestore documents...');
      
      // ✅ ENHANCED: Create public interpreter profile with picture URL
      const interpreterDoc = {
        userId: firebaseUser.uid,
        firstName,
        bio,
        city,
        country,
        location,
        languages: selectedLanguages,
        industries: selectedIndustries,
        hourlyRate: hourlyRateNum ?? 0,
        halfDayRate: halfDayRateNum ?? 0,
        dailyRate: dailyRateNum ?? 0,
        ratePerWord: ratePerWordNum ?? 0,
        ratePerPage: ratePerPageNum ?? 0,
        translationHourlyRate: translationHourlyRateNum ?? 0,
        currency: 'USD',
        timeZone,
        keywords,
        education,
        culturalKnowledge,
        profilePicture: profilePictureURL, // ✅ NEW: Include profile picture URL
        available: true,
        availability: true,
        isVerified: false,
        verificationStatus: 'pending',
        profileCompleteness: 100,
        rating: 0,
        completedSessions: 0,
        totalReviews: 0,
        profileViews: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'interpreters', firebaseUser.uid), interpreterDoc);
      console.log('✅ Public interpreter profile created with picture URL');

      // ✅ CLIENT-SIDE: Create private interpreter profile
      const privateProfile = {
        userId: firebaseUser.uid,
        email,
        lastName,
        whatsapp,
        linkedin,
        accountStatus: 'active',
        emailVerified: firebaseUser.emailVerified,
        showEmail: false,
        showWhatsApp: true,
        showLinkedIn: true,
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        marketingEmails: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'interpreters', firebaseUser.uid, 'private', 'profile'), privateProfile);
      console.log('✅ Private interpreter profile created');

      // ✅ CLIENT-SIDE: Create user document
      const userDoc = {
        uid: firebaseUser.uid,
        email,
        firstName,
        lastName,
        isInterpreter: true,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userDoc);
      console.log('✅ User profile created');

      setRegistrationStep('Verifying profile setup...');
      console.log('🔄 Waiting for profile documents to propagate...');
      
      // ✅ Wait for profile documents to be available
      const profileReady = await waitForProfile(firebaseUser.uid, 10, 500);
      
      if (!profileReady) {
        console.warn('⚠️ Profile documents not confirmed, but continuing...');
      }

      setRegistrationStep('Signing out and preparing login...');
      console.log('🔄 Signing out user for clean login experience...');
      
      // ✅ Sign out the user so they can properly log in
      await signOut(auth);
      
      setRegistrationSuccess(true);
      setRegistrationStep('Registration complete!');
      setSuccessMessage('Account created successfully with profile picture! Redirecting to login page...');
      console.log('🎉 Registration completed successfully with profile picture!');
      
      // ✅ Redirect to login page
      setIsRedirecting(true);
      registrationRedirectTimerRef.current = setTimeout(() => {
        router.push('/login?message=registration-success&email=' + encodeURIComponent(email));
      }, 2000);

    } catch (error: any) {
      console.error('❌ Registration failed:', error);

      let userFriendlyMessage = 'Registration failed. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        userFriendlyMessage = 'This email address is already registered. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        userFriendlyMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        userFriendlyMessage = 'Please enter a valid email address.';
      } else if (error.code === 'permission-denied') {
        userFriendlyMessage = 'Permission denied. Please check your profile information and try again.';
      } else if (error.code?.includes('storage/')) {
        userFriendlyMessage = `Profile picture upload failed: ${error.message}`;
      } else if (error.message.includes('Profile picture') || error.message.includes('Image file')) {
        userFriendlyMessage = error.message;
      } else if (error.message.includes('language') || error.message.includes('industry') || error.message.includes('Bio') || error.message.includes('Education') || error.message.includes('Cultural') || error.message.includes('Keywords')) {
        userFriendlyMessage = error.message;
      } else if (error.message.includes('fill in all required fields')) {
        userFriendlyMessage = error.message;
      }

      setFormError(userFriendlyMessage);
      setRegistrationStep('');

      // Cleanup on error
      if (auth.currentUser && !error.code?.includes('auth/')) {
        console.log('🧹 Cleaning up failed registration...');
        try {
          await auth.currentUser.delete();
        } catch (cleanupError) {
          console.error('Failed to cleanup user:', cleanupError);
        }
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setSuccessMessage(null);

    if (selectedLanguages.length === 0) {
      setFormError('Please select at least one language.');
      return;
    }

    if (selectedIndustries.length === 0) {
      setFormError('Please select at least one industry.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    
    // Add selected arrays to form data
    selectedLanguages.forEach(lang => {
      formData.append('languages', lang);
    });
    selectedIndustries.forEach(industry => {
      formData.append('industries', industry);
    });

    await handleRegistration(formData);
  };

  const handleLanguageChange = (language: string, checked: boolean) => {
    if (checked) {
      setSelectedLanguages(prev => [...prev, language]);
    } else {
      setSelectedLanguages(prev => prev.filter(lang => lang !== language));
    }
    if (checked && formError?.includes('language')) {
      setFormError(null);
    }
  };

  const handleIndustryChange = (industry: string, checked: boolean) => {
    if (checked) {
      setSelectedIndustries(prev => [...prev, industry]);
    } else {
      setSelectedIndustries(prev => prev.filter(ind => ind !== industry));
    }
    if (checked && formError?.includes('industry')) {
      setFormError(null);
    }
  };

  // ✅ Show loading state during authentication check and redirection
  if (authLoading || (isRedirecting && !registrationSuccess)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">
            {authLoading ? 'Checking authentication...' : 'Redirecting...'}
          </p>
        </div>
      </div>
    );
  }

  // ✅ Show success state when registration is complete
  if (registrationSuccess || (isRedirecting && registrationSuccess)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-green-800">Account Created Successfully!</h1>
            <p className="text-muted-foreground">
              Your interpreter profile has been created with your profile picture. You'll be redirected to the login page to sign in with your new credentials.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
          <div className="text-xs text-muted-foreground">
            Redirecting to login page...
          </div>
        </div>
      </div>
    );
  }

  // ✅ Show redirect state for authenticated users
  if (isAuthenticated && user && !registrationSuccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">You're already signed in. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="min-h-screen py-12 px-4 bg-background">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center">
            <Link href="/" className="inline-block">
              <h1 className="text-4xl font-black tracking-tight text-primary hover:text-primary/80 transition-colors">
                Talksie
              </h1>
            </Link>
            <p className="text-muted-foreground mt-2">The Future of Interpreting & Translation</p>
          </div>

          <Card className="w-full shadow-lg border-0 bg-card">
            <form onSubmit={handleSubmit}>
              <CardHeader className="text-center space-y-4">
                <CardTitle className="text-3xl font-bold">Interpreter & Translator Sign Up</CardTitle>
                <CardDescription>
                  Create your profile to connect with clients based on your language skills, industry knowledge, and cultural expertise.
                  Your personal information stays protected, only your name and photo are visible to clients.{' '}
                  <Link href="/about" className="underline hover:text-primary transition-colors" target="_blank">(More info)</Link>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-8">
                {successMessage && (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Success!</AlertTitle>
                    <AlertDescription className="text-green-700">
                      {successMessage}
                    </AlertDescription>
                  </Alert>
                )}

                {formError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Form Error</AlertTitle>
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}

                {isRegistering && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <AlertTitle className="text-blue-800">Creating Account</AlertTitle>
                    <AlertDescription className="text-blue-700">
                      {registrationStep || 'Processing registration...'}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Personal Information */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <User className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Personal Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                      <Input 
                        id="firstName" 
                        name="firstName" 
                        type="text"
                        autoComplete="given-name" 
                        required 
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                      <Input 
                        id="lastName" 
                        name="lastName" 
                        type="text"
                        autoComplete="family-name" 
                        required 
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="Enter your last name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        required 
                        autoComplete="email" 
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input 
                          id="password" 
                          name="password" 
                          type={showPassword ? "text" : "password"} 
                          required 
                          autoComplete="new-password" 
                          disabled={isRegistering}
                          className="h-11 pr-10"
                          placeholder="Create a secure password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={isRegistering}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p className="font-medium">Password requirements:</p>
                        <ul className="list-disc list-inside space-y-0.5 ml-2">
                          <li>At least 6 characters</li>
                          <li>At least one uppercase letter (A-Z)</li>
                          <li>At least one lowercase letter (a-z)</li>
                          <li>At least one special character (!@#$%^&*)</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp Number <span className="text-red-500">*</span></Label>
                      <Input 
                        id="whatsapp" 
                        name="whatsapp" 
                        type="tel"
                        required 
                        autoComplete="tel" 
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="+1234567890"
                      />
                      <p className="text-xs text-muted-foreground">Include country code (e.g., +1 for US/Canada)</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="linkedin">LinkedIn Profile <span className="text-red-500">*</span></Label>
                      <Input 
                        id="linkedin" 
                        name="linkedin" 
                        type="url"
                        required 
                        autoComplete="url" 
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="https://linkedin.com/in/yourname"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className={`${isRegistering ? 'pointer-events-none opacity-50' : ''}`}>
                      <FileUploadField
                        name="profilePicture"
                        label="Profile Picture"
                        maxSize={5}
                        required={true}
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                      />
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>• Supported formats: JPEG, PNG, WebP</p>
                      <p>• Maximum file size: 5MB</p>
                      <p>• Recommended: Square image (1:1 aspect ratio)</p>
                    </div>
                    {isRegistering && (
                      <p className="text-xs text-muted-foreground text-amber-600">
                        File upload disabled during registration process
                      </p>
                    )}
                  </div>
                </div>

                {/* Professional Information */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Clock className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Professional Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        required
                        autoComplete="address-level2"
                        disabled={isRegistering}
                        className="h-11"
                        placeholder="e.g. New York"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country <span className="text-red-500">*</span></Label>
                      <Select name="country" required disabled={isRegistering}>
                        <SelectTrigger id="country" className="h-11">
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {COUNTRIES.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-4 col-span-full">
                      <div>
                        <p className="text-sm font-medium">Rates (fill in at least one)</p>
                      </div>
                      <div className="space-y-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Interpreting</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="hourlyRate">Hourly rate (USD)</Label>
                            <Input id="hourlyRate" name="hourlyRate" type="number" min="0.01" step="0.01" disabled={isRegistering} className="h-11" placeholder="50" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="halfDayRate">Half-day rate up to 4 hours (USD)</Label>
                            <Input id="halfDayRate" name="halfDayRate" type="number" min="0.01" step="0.01" disabled={isRegistering} className="h-11" placeholder="180" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="dailyRate">Daily rate up to 8 hours (USD)</Label>
                            <Input id="dailyRate" name="dailyRate" type="number" min="0.01" step="0.01" disabled={isRegistering} className="h-11" placeholder="320" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Translation</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="ratePerWord">Rate per word (USD)</Label>
                            <Input id="ratePerWord" name="ratePerWord" type="number" min="0.001" step="0.001" disabled={isRegistering} className="h-11" placeholder="0.12" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ratePerPage">Rate per page – 250 words (USD)</Label>
                            <Input id="ratePerPage" name="ratePerPage" type="number" min="0.01" step="0.01" disabled={isRegistering} className="h-11" placeholder="30" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="translationHourlyRate">Hourly rate (USD, optional)</Label>
                            <Input id="translationHourlyRate" name="translationHourlyRate" type="number" min="0.01" step="0.01" disabled={isRegistering} className="h-11" placeholder="45" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeZone">Time Zone <span className="text-red-500">*</span></Label>
                    <Select name="timeZone" required disabled={isRegistering}>
                      <SelectTrigger id="timeZone" className="h-11">
                        <SelectValue placeholder="Select your time zone" />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map(tz => (
                          <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio / Professional Summary <span className="text-red-500">*</span></Label>
                    <Textarea 
                      id="bio" 
                      name="bio" 
                      className="min-h-32" 
                      required 
                      disabled={isRegistering}
                      placeholder="Describe your professional background, experience, and what makes you unique as an interpreter... (minimum 50 characters)"
                    />
                    <p className="text-xs text-muted-foreground">Minimum 50 characters required.</p>
                  </div>
                </div>

                {/* Skills & Expertise */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Users className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Skills & Expertise</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Languages <span className="text-red-500">*</span></Label>
                      <p className="text-sm text-muted-foreground mb-3">Select all languages you are proficient in (at least one required).</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {LANGUAGES.map(lang => (
                          <div key={lang} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`lang-${lang}`} 
                              name="languages" 
                              value={lang} 
                              disabled={isRegistering}
                              checked={selectedLanguages.includes(lang)}
                              onCheckedChange={(checked) => handleLanguageChange(lang, checked as boolean)}
                            />
                            <Label htmlFor={`lang-${lang}`} className="text-sm font-normal cursor-pointer">
                              {lang}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {selectedLanguages.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Selected: {selectedLanguages.join(', ')}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-base font-medium">Industries <span className="text-red-500">*</span></Label>
                      <p className="text-sm text-muted-foreground mb-3">Select your areas of expertise (at least one required).</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {INDUSTRIES.map(industry => (
                          <div key={industry} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`industry-${industry}`} 
                              name="industries" 
                              value={industry} 
                              disabled={isRegistering}
                              checked={selectedIndustries.includes(industry)}
                              onCheckedChange={(checked) => handleIndustryChange(industry, checked as boolean)}
                            />
                            <Label htmlFor={`industry-${industry}`} className="text-sm font-normal cursor-pointer">
                              {industry}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {selectedIndustries.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Selected: {selectedIndustries.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="keywords">Keywords & Specializations <span className="text-red-500">*</span></Label>
                      <Textarea 
                        id="keywords" 
                        name="keywords" 
                        required 
                        disabled={isRegistering}
                        placeholder="conference interpreting, medical translation, legal documents, business meetings..."
                        maxLength={500}
                      />
                      <p className="text-xs text-muted-foreground">Enter specific terms or areas of expertise, separated by commas (minimum 5 characters).</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="education">Education & Certifications <span className="text-red-500">*</span></Label>
                      <Textarea 
                        id="education" 
                        name="education" 
                        required 
                        disabled={isRegistering}
                        placeholder="Your educational background, certifications, and relevant training..."
                        maxLength={1000}
                      />
                      <p className="text-xs text-muted-foreground">Minimum 10 characters required.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="culturalKnowledge">Cultural Knowledge & Experience <span className="text-red-500">*</span></Label>
                      <Textarea 
                        id="culturalKnowledge" 
                        name="culturalKnowledge" 
                        required 
                        disabled={isRegistering}
                        placeholder="Describe your cultural background and cross-cultural communication experience..."
                        maxLength={1000}
                      />
                      <p className="text-xs text-muted-foreground">Minimum 10 characters required.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4 pt-8">
                <SubmitButton isRegistering={isRegistering} />
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <Link href="/login" className="underline hover:text-primary">Sign in here</Link>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    By creating an account, you agree to our{' '}
                    <Link href="/terms-of-service" className="underline hover:text-primary">Terms of Service</Link>
                    {' '}and{' '}
                    <Link href="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>
                  </p>
                </div>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </FormProvider>
  );
}

export default function SignUpPage() {
  return <SignUpForm />;
}
